import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createApprovalPolicy } from "shared";
import { toast } from "sonner";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient, trpc } from "@/lib/trpcClient";
import { ApprovalRulesInput } from "./-components/approval-rules-input";

export const Route = createFileRoute(
  "/_authed/_org/policies/approval/$id/edit",
)({
  beforeLoad: ({ context: { queryClient, trpc }, params: { id } }) => {
    queryClient.ensureQueryData(
      trpc.approvalPolicy.get.queryOptions({ policyId: id }),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();

  const { data: policy } = useSuspenseQuery(
    trpc.approvalPolicy.get.queryOptions({ policyId: id }),
  );

  const { mutate: editPolicy, isPending } = useMutation(
    trpc.approvalPolicy.edit.mutationOptions({
      onError: (error) => {
        toast.error("Failed to edit policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success(`"${policy.name}" successfully edited`);
        navigate({
          to: "/policies/approval",
        });
        form.reset();
        queryClient.invalidateQueries(trpc.approvalPolicy.list.queryOptions());
      },
    }),
  );

  const {
    isDefault: _isDefault,
    id: policyId,
    organizationId: _orgId,
    ...editable
  } = policy;

  const form = useAppForm({
    defaultValues: editable,
    onSubmit: ({ value: values }) => editPolicy({ policyId, values }),
    validators: {
      onSubmit: createApprovalPolicy,
    },
  });

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 py-6 pb-2">
        <SheetTitle>Edit "{policy.name}"</SheetTitle>
        <SheetDescription>
          Approval policies control who needs to review transactions on
          different cards.
        </SheetDescription>
      </SheetHeader>

      <form.AppForm>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          {/* Scrollable content area with fixed max height */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-6">
            <form.AppField
              children={(field) => (
                <field.TextField label="Name" required={true} />
              )}
              name="name"
            />

            <form.AppField
              children={(field) => (
                <field.TextAreaField label="Description" required={false} />
              )}
              name="description"
            />

            <form.Subscribe selector={(state) => state.values.approvers}>
              {(approvers) => (
                <FormStepSeparator
                  description={
                    <div className="flex items-center gap-1.5">
                      <span className="shrink-0 pb-1">
                        Require Approval From
                      </span>

                      {approvers.length > 1 && (
                        <form.AppField name="groupOperator">
                          {(field) => (
                            <field.SelectField
                              className="rounded-md bg-neutral-100 text-lg text-neutral-700 shadow-none [&>button]:px-2"
                              options={[
                                {
                                  label: "Any Of",
                                  value: "any",
                                },
                                {
                                  label: "All Of",
                                  value: "all",
                                },
                              ]}
                            />
                          )}
                        </form.AppField>
                      )}
                    </div>
                  }
                />
              )}
            </form.Subscribe>

            <ApprovalRulesInput
              form={form}
              fields={{
                approvers: "approvers",
                groupOperator: "groupOperator",
              }}
            />
          </div>

          <div className="flex min-h-16 flex-row items-center justify-start space-x-4 border-t p-6">
            <form.ConditionalSubmitCancelButtons isPending={isPending} />
          </div>
        </form>
      </form.AppForm>
    </div>
  );
}
